import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { alpha, useTheme } from "@mui/material/styles";
import ExploreIcon from "assets/icons/explore";
import {
  isDeviceRegisteredForPush,
  registerDeviceForPush,
  startNotificationListener,
} from "functions/firebaseMessaging";
import { NotificationsAll } from "functions/http-requests/notifications";
import RouteConstant from "navigation/constant";
import MainContainer from "parts/mainContainer";
import Notifications from "parts/notifications";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { RootState } from "state/store";

const TopHeader = () => {
  const { palette } = useTheme();
  const { hash, pathname } = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useSelector((state: RootState) => state.user);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openNotifications, setOpenNotifications] = useState<boolean>(false);
  const [unreadNotificationCount, setUnreadNotificationCount] =
    useState<number>(0);
  const [notificationsLoading, setNotificationsLoading] =
    useState<boolean>(false);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshNotifications = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setNotificationsLoading(true);
      const result = await NotificationsAll({
        DATA: {
          pageNumber: 1,
          pageSize: 25,
          unreadOnly: true,
        },
      });
      const unreadCount: number = result?.data?.meta?.unreadCount || 0;
      setUnreadNotificationCount(unreadCount);
    } catch (err) {
      console.error("[notifications] fetch failed", err);
    } finally {
      if (showSpinner) setNotificationsLoading(false);
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshNotifications(false);
    }, 250);
  }, [refreshNotifications]);
  const handleClickNotifications = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);
    setOpenNotifications(true);
  };

  const handleCloseNotifications = () => {
    setAnchorEl(null);
    setOpenNotifications(false);
  };

  const id = openNotifications ? "notifications-popover" : undefined;

  useEffect(() => {
    if (!isLoggedIn) return;

    let unsubscribe: (() => void) | undefined;

    refreshNotifications(false);

    const handleNotificationClick = (url: string) => {
      if (!url) return;
      if (/^https?:\/\//i.test(url)) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        navigate(url);
      }
    };

    const setupPushMessaging = async () => {
      const alreadyRegistered = isDeviceRegisteredForPush();

      if (alreadyRegistered) {
        unsubscribe = await startNotificationListener({
          onNotification: scheduleRefresh,
          onNotificationClick: handleNotificationClick,
        });

        return;
      }

      const registrationResult = await registerDeviceForPush();
      if (registrationResult.ok) {
        unsubscribe = await startNotificationListener({
          onNotification: scheduleRefresh,
          onNotificationClick: handleNotificationClick,
        });
      } else {
        console.warn(
          "[FCM device register failed]",
          registrationResult.step,
          registrationResult.message,
        );
      }
    };

    setupPushMessaging().catch((error) => {
      console.error("[FCM setup error]", error);
    });

    // Self-healing: refresh from backend whenever the tab becomes visible.
    // This catches any background message that didn't reach the page (e.g.
    // SW postMessage was dropped because no tab was open at the time).
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshNotifications(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      unsubscribe?.();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [isLoggedIn, refreshNotifications, scheduleRefresh, navigate]);

  const isActiveLink = useCallback(
    (link: string) => {
      if (link.includes("#")) {
        const [path, linkHash] = link.split("#");
        return pathname === path && `#${linkHash}` === hash;
      }
      return !hash && pathname === link;
    },
    [pathname, hash],
  );

  const topBarList = [
    {
      label: "Build your bundle",
      link: RouteConstant.PRODUCT_BUILD_BUNDLE,
    },
    {
      label: "Premium Plans",
      link: RouteConstant.PRODUCT_PREMIUM_PLANS,
    },
    {
      label: "Tourist Plans",
      link: RouteConstant.PRODUCT_TOURIST_PLANS,
    },
    {
      label: "Promotion",
      link: RouteConstant.PROMOTION,
    },
  ];
  return (
    <>
      <MainContainer>
        <Box py={1}>
          <Grid
            container
            spacing={{ xs: 0, sm: 2 }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item xs={12} sm={8}>
              <List
                disablePadding
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: {
                    xs: "center",
                    sm: "flex-start",
                  },
                  columnGap: { xs: 1, sm: 2 },
                  rowGap: { xs: 0.5, sm: 2 },
                }}
              >
                {topBarList?.map((item, i) => (
                  <ListItem
                    key={i}
                    sx={{
                      p: 0,
                      width: "auto",
                      lineHeight: 1,
                    }}
                  >
                    <NavLink to={item.link}>
                      <Box
                        component="span"
                        textTransform="uppercase"
                        fontSize={{ xs: 12, sm: 14 }}
                        fontWeight={{ sm: 600 }}
                        className="fm-poppins"
                        sx={{
                          color: isActiveLink(item.link)
                            ? palette.primary.main
                            : palette.secondary3.main,
                          display: "inline-block",
                          lineHeight: 1,
                          "&:hover": {
                            color: palette.primary.main,
                          },
                        }}
                      >
                        {item?.label}
                      </Box>
                    </NavLink>
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "center",
                  sm: "flex-end",
                },
                alignItems: "center",
                gap: 2,
              }}
            >
              {isLoggedIn ? (
                <Box>
                  <CustomIconButton
                    Icon={
                      <Badge
                        badgeContent={unreadNotificationCount}
                        color="error"
                        max={99}
                      >
                        <NotificationsOutlinedIcon />
                      </Badge>
                    }
                    onClick={handleClickNotifications}
                  />

                  <Notifications
                    id={id}
                    anchorEl={anchorEl}
                    open={openNotifications}
                    onClose={handleCloseNotifications}
                    loading={notificationsLoading}
                    onChanged={() => refreshNotifications(false)}
                  />
                </Box>
              ) : null}

              <Link to={RouteConstant.PRODUCTS}>
                <Button
                  variant="text"
                  startIcon={<ExploreIcon width={14} />}
                  color="inherit"
                  size="small"
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  sx={{
                    color: palette.common.black,
                    fontSize: { xs: 14, sm: 18 },
                    p: 0,
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  Explore Talkio
                </Button>
              </Link>
            </Grid>
          </Grid>
        </Box>
      </MainContainer>
    </>
  );
};

export default TopHeader;

interface CustomIconButtonProps {
  Icon?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: React.ReactNode;
}

const CustomIconButton: React.FC<CustomIconButtonProps> = ({
  Icon,
  onClick,
  children,
}) => {
  const { palette } = useTheme();

  return (
    <IconButton
      onClick={onClick}
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        p: 0,
        border: `1px solid ${alpha(palette.secondary.main, 0.2)}`,
        backgroundColor: alpha(palette.secondary.main, 0.05),
        color: palette.common.black,

        "&:hover, &:focus": {
          backgroundColor: alpha(palette.secondary.main, 0.05),
        },
      }}
    >
      {Icon ? Icon : null}
      {children}
    </IconButton>
  );
};
